// Shared helpers for the /tasks catalog page and its supporting cards
// (TemplatePinCard, TaskCardCalendar, etc). Framework-free so any
// component can import without dragging React in.

// Soft category accents (icon tint only on cards) come from the shared
// `colorForSlug` / `paletteForSlug` hash so a task matches other catalogs.
export {
  colorForSlug,
  paletteForSlug,
  heroChromaForSlug,
  hoverSurfaceForSlug,
  breadcrumbHoverTintForSlug,
} from './agent_catalog';

// Same 7-day "new" rule the rest of the app uses.
export const isNewTemplate = (template) => {
  if (!template?.createdAt) return false;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 7);
  return new Date(template.createdAt) > cutoff;
};

// Schedule label that mirrors what TaskCard renders, but tiny and shareable.
const SCHEDULE_LABELS = {
  once: 'One-time',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  annually: 'Yearly',
};

const TIME_OF_DAY_LABELS = {
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
};

/** Discovery chips under search / hero — same visual language as /models (see `SUGGESTIONS`). */
export const TASK_TRY_CHIPS = [
  { label: 'Social content', query: 'social', icon: 'hashtag', color: '#10B981' },
  { label: 'Meeting notes', query: 'meeting', icon: 'microphone', color: '#F59E0B' },
  { label: 'Blog draft', query: 'blog', icon: 'pen-nib', color: '#A855F7' },
  { label: 'Image ad', query: 'image', icon: 'image', color: '#EC4899' },
  { label: 'Code review', query: 'code', icon: 'code', color: '#14B8A6' },
  { label: 'Daily summary', query: 'summary', icon: 'file-lines', color: '#3B82F6' },
];

export const scheduleLabel = (template) => {
  const sched = template?.suggestedSchedule || template?.schedule;
  if (!sched) return 'Any time';
  const cadence = SCHEDULE_LABELS[sched.cadence] || SCHEDULE_LABELS[sched.frequency] || '';
  const tod = TIME_OF_DAY_LABELS[sched.timeOfDay] || '';
  return [cadence, tod].filter(Boolean).join(' · ') || 'Any time';
};

// Group templates by their populated category. Returns an array of
// `{ category, templates }` ordered by the category list passed in so the
// rail/page order stays predictable across views.
export const groupByCategory = (templates, categories) => {
  if (!Array.isArray(templates) || !templates.length) return [];
  const buckets = new Map();
  templates.forEach((tpl) => {
    const slug = tpl?.category?.slug || tpl?.category || 'uncategorized';
    if (!buckets.has(slug)) buckets.set(slug, []);
    buckets.get(slug).push(tpl);
  });
  const ordered = [];
  (categories || []).forEach((cat) => {
    const slug = cat?.slug;
    if (slug && buckets.has(slug)) {
      ordered.push({ category: cat, templates: buckets.get(slug) });
      buckets.delete(slug);
    }
  });
  // Anything left over (uncategorized or unknown) goes at the end.
  buckets.forEach((list, slug) => {
    ordered.push({ category: { slug, name: slug }, templates: list });
  });
  return ordered;
};

// ─── Saved-task helpers (used by TaskCardCalendar) ──────────────────────────
// A user's saved task has `schedule.type` instead of the template-flavored
// `suggestedSchedule`. The card surfaces the cadence so accent + icon +
// "DAILY · 8:00 AM"-style headlines all hash off the same source.

const TASK_SCHEDULE_ACCENTS = {
  daily:    '#F59E0B',
  weekly:   '#0EA5E9',
  monthly:  '#7C3AED',
  annually: '#16A34A',
  once:     '#9333EA',
};

const TASK_SCHEDULE_ICONS = {
  daily:    'sun',
  weekly:   'calendar-week',
  monthly:  'calendar',
  annually: 'calendar-day',
  once:     'clock',
};

const TASK_SCHEDULE_NAMES = {
  daily:    'Daily',
  weekly:   'Weekly',
  monthly:  'Monthly',
  annually: 'Yearly',
  once:     'One-time',
};

export const taskScheduleAccent = (task) =>
  TASK_SCHEDULE_ACCENTS[task?.schedule?.type] || TASK_SCHEDULE_ACCENTS.once;

export const taskScheduleIcon = (task) =>
  TASK_SCHEDULE_ICONS[task?.schedule?.type] || TASK_SCHEDULE_ICONS.once;

const formatTaskTime = (time) => {
  if (!time) return '';
  if (typeof time === 'string') return time;
  const { hour, minute } = time;
  if (hour == null) return '';
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const h = hour % 12 || 12;
  const m = String(minute || 0).padStart(2, '0');
  return `${h}:${m} ${suffix}`;
};

// "DAILY · 8:00 AM" — used as the loud eyebrow on the calendar tile.
export const taskScheduleHeadline = (task) => {
  const sched = task?.schedule;
  if (!sched?.type) return 'ONE-TIME';
  const cadence = (TASK_SCHEDULE_NAMES[sched.type] || sched.type).toUpperCase();
  const time = formatTaskTime(sched.timeOfDay);
  return time ? `${cadence} · ${time}` : cadence;
};

// Friendly relative time for "Next 8h" / "Last 2d".
export const formatRelative = (iso) => {
  if (!iso) return '';
  const ts = new Date(iso).getTime();
  const diff = ts - Date.now();
  const abs = Math.abs(diff);
  const mins = Math.round(abs / 60000);
  const hours = Math.round(mins / 60);
  const days = Math.round(hours / 24);
  const past = diff < 0;
  if (mins < 1) return past ? 'just now' : 'in a moment';
  if (mins < 60) return past ? `${mins}m ago` : `in ${mins}m`;
  if (hours < 24) return past ? `${hours}h ago` : `in ${hours}h`;
  if (days < 7) return past ? `${days}d ago` : `in ${days}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// Compact format for usage counts. 1234 → "1.2k".
export const formatCount = (n) => {
  if (!n || n < 1000) return String(n || 0);
  if (n < 1_000_000) {
    const v = n / 1000;
    return `${v >= 10 ? Math.round(v) : v.toFixed(1)}k`;
  }
  return `${(n / 1_000_000).toFixed(1)}M`;
};

// ─── Tasks catalog hero copy (shared by /tasks + CatalogDiscoveryHero) ─────

/** Strip trailing site suffixes so SEO titles read well as on-page headlines. */
export function cleanCategoryHeroTitle(raw) {
  if (!raw?.trim()) return null;
  let s = raw.trim();
  s = s.replace(/\s*[—–|]\s*Gab AI\s*$/i, '').replace(/\s*AI Task Templates\s*$/i, '').trim();
  if (!s || s.length > 88) return null;
  return s;
}

/**
 * @param {object | null} discoveryCategory — active task template category record, or null on “all”.
 * @param {function} t — `useTranslation().t`
 */
export function tasksDiscoveryHeadline(discoveryCategory, t) {
  const isCategoryMode = !!(discoveryCategory?.slug);
  if (!isCategoryMode) {
    return t('pages.tasks.discoveryHeadline')
      || 'Put AI to work for you with automated tasks.';
  }
  const name = discoveryCategory?.name?.trim() || 'Templates';
  const fromMeta = cleanCategoryHeroTitle(discoveryCategory?.meta?.title);
  if (fromMeta) return fromMeta;
  return t('pages.tasks.discoveryHeadlineCategory', { name })
    || `What should run next for ${name}?`;
}

export function tasksDiscoverySubtitle(discoveryCategory, t) {
  const isCategoryMode = !!(discoveryCategory?.slug);
  if (!isCategoryMode) {
    return t('pages.tasks.discoverySubtitle')
      || 'A task is a reusable instruction you schedule once—then it runs automatically and delivers results where you want them.';
  }
  const md = discoveryCategory?.meta?.description?.trim();
  const d = discoveryCategory?.description?.trim();
  return md || d || (
    t('pages.tasks.discoverySubtitleCategoryFallback', {
      name: discoveryCategory?.name?.trim() || 'this category',
    })
    || `Tap any chip to open setup for a ${discoveryCategory?.name || 'this category'} template—pick a schedule and delivery, and Gab AI handles the rest.`
  );
}

/**
 * Rows for template chips (same rules as the former TasksHero discovery row).
 * @returns {Array<object>} template-like objects with at least `id`, `slug?`, `name`, `icon?`.
 */
export function tasksDiscoveryTemplateRows(discoveryCategory, discoveryChipTemplates, templates) {
  const isCategoryMode = !!(discoveryCategory?.slug);
  if (isCategoryMode && discoveryChipTemplates != null) {
    return discoveryChipTemplates;
  }
  const list = (templates || []).slice(0, 6);
  if (list.length > 0) return list;
  return [
    { id: 'd1', name: 'Brief me on AI news', icon: 'newspaper' },
    { id: 'd2', name: 'Plan my week', icon: 'calendar-check' },
    { id: 'd3', name: 'Translate emails', icon: 'language' },
    { id: 'd4', name: 'Generate ideas', icon: 'lightbulb' },
    { id: 'd5', name: 'Summarize my notes', icon: 'note-sticky' },
    { id: 'd6', name: 'Draft a blog post', icon: 'pen-nib' },
  ];
}
